import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UserPlus, Loader2, ShieldX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function InviteAccept() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | error | done
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      localStorage.setItem("routerev_invite", code);
      navigate("/login", { replace: true });
      return;
    }
    api.get(`/friends/invite/${code}`)
      .then((r) => { setPreview(r.data); setState("ready"); })
      .catch((e) => { setError(e.response?.data?.detail || "This invite link is invalid."); setState("error"); });
  }, [code, user, loading, navigate]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post("/friends/invite/accept", { code });
      toast.success(`You're now friends with ${r.data.name}`);
      navigate("/friends", { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || "Could not accept this invite.");
      setState("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary px-4" data-testid="invite-accept-page">
      <div className="bg-white border border-border rounded-2xl p-8 max-w-sm w-full text-center">
        {state === "loading" && (
          <div className="py-6"><Loader2 className="animate-spin mx-auto text-brand" size={28} /></div>
        )}
        {state === "error" && (
          <div>
            <ShieldX size={32} strokeWidth={1.5} className="mx-auto text-destructive mb-3" />
            <h1 className="font-display text-xl font-semibold mb-1">Invite unavailable</h1>
            <p className="text-sm text-muted-foreground mb-5" data-testid="invite-error">{error}</p>
            <button data-testid="invite-home-btn" onClick={() => navigate("/friends", { replace: true })}
              className="bg-brand text-white rounded-md px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-colors">
              Go to Friends
            </button>
          </div>
        )}
        {state === "ready" && preview && (
          <div>
            <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand mb-3">Friend invite</div>
            <div className="w-16 h-16 rounded-full bg-brand text-white flex items-center justify-center text-2xl font-bold mx-auto mb-3">
              {preview.name?.[0]}
            </div>
            <h1 className="font-display text-2xl font-semibold">{preview.name}</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-6">wants to connect with you on jeeo.</p>
            <button
              data-testid="invite-accept-btn"
              onClick={accept}
              disabled={busy || preview.is_self}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white rounded-md px-5 py-3 text-sm font-semibold hover:opacity-90 transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <UserPlus size={16} strokeWidth={1.5} />}
              {preview.is_self ? "This is your own link" : "Accept & become friends"}
            </button>
            <button data-testid="invite-decline-btn" onClick={() => navigate("/friends", { replace: true })}
              className="w-full mt-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2">
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
